"use client";

import { cn } from "@ui/lib";
import { CheckIcon, CopyIcon } from "lucide-react";
import { useState } from "react";

/**
 * A snippet with a copy button: the MCP URL, a command, a request body or a
 * whole script (`wrap` lets long text break across lines). The text is always
 * visible and selectable, so a blocked clipboard costs nothing.
 */
export function CopyBlock({
	text,
	wrap = false,
	label = "Copy",
	className,
}: {
	text: string;
	wrap?: boolean;
	label?: string;
	className?: string;
}) {
	const [copied, setCopied] = useState(false);

	const handleCopy = async () => {
		try {
			await navigator.clipboard.writeText(text);
			setCopied(true);
			setTimeout(() => setCopied(false), 2000);
		} catch {
			// Clipboard blocked; the text stays visible to select by hand.
		}
	};

	return (
		<div
			className={cn(
				"flex items-start gap-3 rounded-lg border bg-muted/50 px-3 py-2.5",
				className,
			)}
		>
			<code
				className={cn(
					"min-w-0 flex-1 font-mono text-xs leading-relaxed text-foreground",
					wrap ? "whitespace-pre-wrap break-words" : "truncate",
				)}
			>
				{text}
			</code>
			<button
				type="button"
				onClick={handleCopy}
				className="flex shrink-0 items-center gap-1 rounded-md px-1.5 py-0.5 text-xs font-semibold text-muted-foreground hover:bg-background hover:text-foreground"
			>
				{copied ? (
					<CheckIcon className="size-3.5" aria-hidden />
				) : (
					<CopyIcon className="size-3.5" aria-hidden />
				)}
				<span aria-live="polite">{copied ? "Copied" : label}</span>
			</button>
		</div>
	);
}
